import React, { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const record = tab => id => "/admin/clients/" + id + "?tab=" + tab;
const cockpit = section => id => "/admin/cockpit/" + id + "/" + section;
const AREAS = {
  "Kontakte": { hint: "Kontaktpersonen in der bestehenden Kundenakte pflegen.", action: "Kontaktpersonen öffnen", destination: record("kontakte") },
  "Dateien & Freigaben": { hint: "Dokumente beim richtigen Kunden öffnen und Freigaben bewusst prüfen.", action: "Dokumente öffnen", destination: record("documents") },
  "Besuche & Performance": { hint: "Datenquelle und Zeitraum beim gewählten Kunden prüfen. Fehlende Messung bleibt als unbekannt erkennbar.", action: "Kundenstatistik öffnen", destination: cockpit("performance") },
  "Ideen & Verbesserungen": { hint: "Vorhandene Vorschläge beim gewählten Kunden prüfen und gezielt freigeben.", action: "Kundenideen öffnen", destination: cockpit("next-steps") },
  "Bewertungen": { hint: "Bewertungsaktionen beim gewählten Kunden verwalten.", action: "Bewertungen öffnen", destination: record("bewertungen") },
  "Abos & Pakete": { hint: "Die tatsächlich vereinbarten Konditionen beim Kunden prüfen.", action: "Abo & Vertrag öffnen", destination: record("abo") },
  "Zahlungen": { hint: "Rechnungsstatus im vorhandenen Bereich prüfen. Eine Zahlung wird hier nicht automatisch bestätigt.", action: "Rechnungen öffnen", destination: record("invoices") },
  "Zusatzleistungen": { hint: "Vorhandene Module und vereinbarte Zusatzleistungen beim Kunden prüfen.", action: "Konditionen öffnen", destination: record("abo") },
};

export default function AdminPlaceholder({ title }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const selectId = useId();
  const area = AREAS[title];
  const hasArea = Boolean(area);
  const allowed = ["super_admin", "team_member"].includes(user?.role);
  const scope = JSON.stringify([user?.id || user?.email || null, user?.role || null]);
  const requestId = useRef(0);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ scope: null, status: "loading", clients: [] });
  const [selection, setSelection] = useState({ scope: null, id: "" });

  useEffect(() => {
    const request = ++requestId.current;
    let cancelled = false;
    setSelection({ scope, id: "" });
    if (!allowed || !hasArea) return () => { cancelled = true; };
    setState({ scope, status: "loading", clients: [] });
    Promise.resolve().then(() => api.get("/clients")).then(response => {
      if (cancelled || request !== requestId.current) return;
      const clients = response?.data;
      if (!Array.isArray(clients) || clients.some(c =>
        !c || typeof c.id !== "string" || !c.id.trim() ||
        typeof c.company_name !== "string" || !c.company_name.trim()
      ) || new Set(clients.map(c => c.id)).size !== clients.length) throw new Error("Invalid client list");
      setState({ scope, status: "ready", clients: clients.map(({ id, company_name }) => ({ id, company_name })) });
    }).catch(() => {
      if (!cancelled && request === requestId.current) setState({ scope, status: "error", clients: [] });
    });
    return () => { cancelled = true; };
  }, [scope, allowed, hasArea, attempt]);

  const current = state.scope === scope;
  const clients = allowed && current && state.status === "ready" ? state.clients : [];
  const selectedId = selection.scope === scope && clients.some(c => c.id === selection.id) ? selection.id : "";
  const loading = allowed && (!current || state.status === "loading");
  const error = current && state.status === "error";

  return (
    <div className="fade-in w-full min-w-0" data-testid="admin-placeholder">
      <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="admin-placeholder-title">{t(title)}</h1>
      {area && <p className="text-gray-400 mb-6">{t(area.hint)}</p>}
      {!area ? <p>{t("Diese Funktion ist noch nicht verfügbar.")}</p> : !allowed ? (
        <p role="alert">{t("Kein Zugriff")}</p>
      ) : (
        <form className="max-w-4xl rounded-xl border border-white/10 bg-[#121217] p-4 sm:p-6"
          onSubmit={event => {
            event.preventDefault();
            if (selectedId) nav(area.destination(encodeURIComponent(selectedId)));
          }}>
          <label htmlFor={selectId} className="block font-bold mb-2">{t("1 · Kunde auswählen")}</label>
          {loading && <p role="status" className="text-sm text-gray-400">{t("Kunden werden geladen…")}</p>}
          {error && <div role="alert" className="text-sm text-red-300">
            <p>{t("Kunden konnten nicht geladen werden.")}</p>
            <button type="button" onClick={() => setAttempt(value => value + 1)} data-testid="area-clients-retry"
              className="mt-3 min-h-[44px] rounded-lg border border-white/20 px-4 py-2 font-semibold">{t("Erneut versuchen")}</button>
          </div>}
          {!loading && !error && clients.length === 0 && <p role="status" className="text-sm text-gray-400">{t("Keine Kunden verfügbar.")}</p>}
          <select id={selectId} data-testid="area-client-select" required disabled={loading || error || clients.length === 0}
            value={selectedId} onChange={event => setSelection({ scope, id: event.target.value })}
            className="block w-full max-w-xl min-h-[44px] rounded-lg border border-white/10 bg-black px-4 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#A3E635] disabled:opacity-50">
            <option value="">{t("Bitte Kunde auswählen")}</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
          <h2 className="font-bold mt-6 mb-2">{t("2 · Im vorhandenen Bereich weiterarbeiten")}</h2>
          <p className="text-sm text-gray-400 mb-4">{t("Die Kundenakte bleibt der gemeinsame Ort für Ihr Team. Dort sehen Sie den passenden Kundenkontext und die vorhandenen Daten.")}</p>
          <button type="submit" disabled={!selectedId} data-testid="area-open-client"
            className="inline-flex max-w-full min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[#A3E635] px-5 py-3 font-bold text-black hover:bg-[#b6f15a] disabled:opacity-40 disabled:cursor-not-allowed">
            <span className="min-w-0 break-words">{t(area.action)}</span><ArrowRight className="h-5 w-5 shrink-0" aria-hidden="true" />
          </button>
          <p className="mt-5 border-t border-white/10 pt-4 text-xs text-amber-300">{t("Die globale Sammelansicht ist noch nicht verfügbar. Hier arbeiten Sie im vorhandenen Kundenbereich.")}</p>
        </form>
      )}
    </div>
  );
}
